import {BaseProjectStrategy} from './base-project-strategy';
import {ApiService} from '../services/api.service';
import {Request, RequestWithDuplicata} from './request';
import {Document} from './user';
import {PrintService} from '../services/print.service';
import {StatusLabel} from './print';
import {AppEnvironment} from 'src/environments/environment.model';
import {RequestService} from '../services/request.service';
import {VisaFN, VisaFormConfig, VisaFormName} from './forms';
import {PASS_NOTICE_FORM} from './pass/notice-form';

export enum CountryCat {
  ALL = 'ALL'
}

/**
 * Intermediate abstract class for all PASS-* environments.
 *
 * Factors out the behaviour that is identical across every passport-issuance
 * project (single ALL country-category, no documents, no sticker printing,
 * no pre-approval, and the standard ENTRY-VISA label).
 *
 * Concrete implementations must still provide:
 *   referenceSchema, documentTypeMap, tableRequestsConfig,
 *   loadFormConfigs, getFormConfig, getVisaTypes, getRequestData,
 *   buildAdminRequestsTableTransformer, buildSearchParamsForPayments
 */
export abstract class PassProjectStrategy extends BaseProjectStrategy {

  /** Shared country-category bucket, populated lazily by setCountries(). */
  protected COUNTRY_CATS: {[id: string]: {value: string; name: string}[]} = {};

  /** Default notice form for pass projects; override in the project strategy when needed. */
  override getNoticeFormConfig(): VisaFormConfig {
    return PASS_NOTICE_FORM;
  }

  /**
   * Name of the JSON-Schema property that holds the nationality enum.
   * Most PASS envs use 'Nationality'; override in the subclass when the
   * schema uses a different property (e.g. PASS-ZIM uses 'CountryOfBirth').
   */
  protected readonly nationalitySchemaProperty: string = 'Nationality';

  // --- Country ---

  setCountries(api: ApiService): Promise<boolean> {
    if(Object.getOwnPropertyNames(this.COUNTRY_CATS).length === 0) {
      return api.schema(this.referenceSchema).then(res => {
        const nationalities = res?.properties?.[this.nationalitySchemaProperty];
        this.COUNTRY_CATS[CountryCat.ALL] = nationalities?.enum.map((n: string) => ({value: n, name: api.lang.transform('DYN.' + n)}));
        return true;
      });
    }
    return Promise.resolve(true);
  }

  isCountryCat(_country: string, _cat: string): boolean {
    // PASS projects use a single ALL bucket – every country falls into it.
    return true;
  }

  setCurrentCountryCat(current: {countryCat?: string; indexed?: unknown}): string | undefined {
    current.countryCat = CountryCat.ALL;
    return current.countryCat;
  }

  getCountries(_previousVisaRequiredOn: boolean): {value: string; name: string}[] {
    return [...(this.COUNTRY_CATS[CountryCat.ALL] || [])];
  }

  // --- Documents / eligibility ---

  override canHaveDocuments(_current: RequestWithDuplicata): boolean {
    // PASS projects do not use the visa-document upload flow.
    return false;
  }

  override isRequirePreapproval(_req: RequestWithDuplicata, _env: AppEnvironment, _request?: Request): boolean {
    return false;
  }

  override getExtensionType(paymentRequests: Document[], _meta: unknown): Document | undefined {
    return paymentRequests[0];
  }

  // --- Print ---

  override getStatusLabelsFromRequest(_req: RequestWithDuplicata | Request, _print: PrintService): StatusLabel | null {
    return {title: 'ENTRY VISA', stickerLabel: 'VISA'};
  }

  // --- Payment ---

  /**
   * The form step where REGISTRATION_PORT_ID is stored for this environment.
   * Override in subclasses that use a different step (e.g. MISCELLANEOUS_DETAILS, APPLIER_INFORMATION).
   */
  protected readonly registrationPortIdStep: VisaFormName = VisaFormName.PERSONAL_DETAILS;

  override isPortOnlinePayable(self: RequestService, request: Request): boolean {
    if(!request || !request.formConfig || !self?.cart?.currentApplication) return false;
    if(self.cart.currentApplication.isRevision) return false;
    const paidOnline = request.visaType?.paidOnline;
    if(typeof paidOnline === 'boolean') return paidOnline;
    if(typeof paidOnline === 'string') {
      const enrollementPort = self.getFieldValue(this.registrationPortIdStep, VisaFN.REGISTRATION_PORT_ID, request.formConfig)?.toUpperCase();
      return enrollementPort ? eval(paidOnline)({RegistrationPortId: enrollementPort}) : false;
    }
    return false;
  }
}
